import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

type Translations = Record<string, unknown>;
type SupportedLocale = 'de' | 'fr';

@Injectable({
  providedIn: 'root'
})
export class LocaleService {
  private readonly _http = inject(HttpClient);
  private readonly _storageKey = 'userLanguagePreference';
  private readonly _translations = signal<Translations>({});

  public readonly currentLocale = signal<SupportedLocale>('de');
  public readonly isLoaded = signal<boolean>(false);

  public async loadTranslations(): Promise<void> {
    const locale = this._detectLocale();
    this.currentLocale.set(locale);

    try {
      const translations = await firstValueFrom(
        this._http.get<Translations>(`/locale/${locale}.json`)
      );
      this._translations.set(translations);
      this.isLoaded.set(true);
    } catch (error) {
      console.error('Failed to load translations:', error);
      // Fallback to German
      try {
        const translations = await firstValueFrom(this._http.get<Translations>('/locale/de.json'));
        this._translations.set(translations);
        this.currentLocale.set('de');
        this.isLoaded.set(true);
      } catch (fallbackError) {
        console.error('Failed to load fallback translations:', fallbackError);
        this.isLoaded.set(true);
      }
    }
  }

  public async switchLanguage(locale: SupportedLocale): Promise<void> {
    localStorage.setItem(this._storageKey, locale);
    await this.loadTranslations();
  }

  public translate(key: string, params?: Record<string, string>): string {
    const keys = key.split('.');
    let value: unknown = this._translations();

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        return key; // Return key if translation not found
      }
    }

    let result = typeof value === 'string' ? value : key;

    // Replace parameters in the format {{paramName}}
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        result = result.replace(new RegExp(`\\{\\{${paramKey}\\}\\}`, 'g'), paramValue);
      });
    }

    return result;
  }

  public getApiLanguage(): 'deCH' | 'frCH' {
    const locale = this.currentLocale();
    return locale === 'de' ? 'deCH' : 'frCH';
  }

  private _detectLocale(): SupportedLocale {
    // 1. Check localStorage first (persisted user selection)
    const stored = localStorage.getItem(this._storageKey);
    if (stored && (stored === 'de' || stored === 'fr')) {
      return stored as SupportedLocale;
    }

    // 2. Get language from Office.js
    const officeLanguage = Office.context?.displayLanguage;

    if (!officeLanguage) {
      return 'de'; // Default to German
    }

    // Normalize to 2-letter code (de-CH -> de, fr-FR -> fr)
    const normalizedLanguage = officeLanguage.substring(0, 2).toLowerCase();

    // Check if it's a supported locale
    if (normalizedLanguage === 'de' || normalizedLanguage === 'fr') {
      return normalizedLanguage;
    }

    // 3. Default to German for unsupported languages
    return 'de';
  }
}
